import React, {
    FormEvent,
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { NameValuePair } from '@gumtree/shared/src/model/generic.model';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { Button, Link } from '@gumtree/ui-library';
import * as sendMessageUtil from '@gumtree/ui-library/src/send-message/util';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import useSessionStorage from '@gumtree/ui-library/src/common-hooks/use-session-storage';
import { navigate } from '@gumtree/shell/src/common/common-actions';
import { useModal } from '@gumtree/shell/src/use-modal';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import HintTooltip from '@gumtree/ui-library/src/hint-tooltip/hint-tooltip';
import getRequestId from '@gumtree/shell/src/request-id';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

import type { VipState, VipUseDispatch } from '../../../reducers';
import * as util from './util';
import {
    Errors,
    PhoneErrors,
    MessageForm,
    MessageInput,
    MessageTextArea,
    MotorsFeedSubtitle,
    ProSellerSubtitle,
    Spinner,
} from './message.style';
import { MotorsCTAContext } from '../../../vip-context';

export type FormType = 'desktop' | 'mobile' | 'mobile-pro';

export const MessageFormComponent: React.FC<{
    formRef: React.RefObject<HTMLFormElement>;
    formType: FormType;
    canOffer: boolean;
    hasError: boolean;
    isMobile: boolean;
    savedMessage: string | null;
    isCurrentUserSeller: boolean;
    updateMessage: (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    errors: NameValuePair[];
    proAccount: boolean;
    hasPhone: boolean;
    isMotors: boolean;
    savedPhone: string | null;
    isPhoneValid: boolean;
    updatePhone: (e: FormEvent<HTMLInputElement>) => void;
    onPhoneInputBlur: () => void;
    sendState: 'unsent' | 'sending' | 'sent';
    mounted: boolean;
    sendMessage: (e: MouseEvent<HTMLButtonElement>) => void;
    isFromMotorsFeed: boolean;
    isPhoneRequired: boolean;
}> = ({
    formRef,
    formType,
    canOffer,
    hasError,
    isMobile,
    savedMessage,
    isCurrentUserSeller,
    updateMessage,
    errors,
    hasPhone,
    savedPhone,
    isPhoneValid,
    updatePhone,
    onPhoneInputBlur,
    sendState,
    mounted,
    proAccount,
    sendMessage,
    isFromMotorsFeed,
    isPhoneRequired,
}) => {
    const buttonDisplayExp = 'primary';

    return (
        <MessageForm
            ref={formRef}
            formType={formType}
            canOffer={canOffer}
            hasError={hasError}
        >
            <fieldset name="provide-message">
                {isMobile ? (
                    <MessageInput
                        type="text"
                        name="form-message"
                        value={savedMessage || ''}
                        placeholder="Is this available?"
                        aria-required
                        disabled={isCurrentUserSeller}
                        autoComplete="off"
                        onChange={updateMessage}
                    />
                ) : (
                    <>
                        <MessageTextArea
                            error={!!errors.length}
                            name="form-message"
                            placeHolder="Is this available?"
                            aria-required
                            id="textarea-message"
                            required
                            disabled={isCurrentUserSeller}
                            value={savedMessage || ''}
                            autoComplete="off"
                            onChange={updateMessage}
                        />
                    </>
                )}
            </fieldset>
            {hasError && (
                <Errors>
                    {errors.map((formError) => (
                        <li key={formError.name}>{formError.value}</li>
                    ))}
                </Errors>
            )}
            {(proAccount || isFromMotorsFeed) && hasPhone && (
                <fieldset name="provide-phone" className={!isPhoneValid ? 'error' : ''}>
                    <input
                        type="tel"
                        name="form-phone"
                        id="form-phone"
                        placeholder={isPhoneRequired ? 'Your phone number (required)' : 'Your phone number'}
                        onChange={updatePhone}
                        onBlur={onPhoneInputBlur}
                        value={savedPhone || ''}
                    />
                    {!isPhoneValid && (
                        <PhoneErrors>Invalid phone number</PhoneErrors>
                    )}

                    {isFromMotorsFeed ? (
                        <MotorsFeedSubtitle>
                            This listing is powered by our dealer network partner, MOTORS. By
                            clicking 'Send message', you agree for us to share your details with the
                            dealership and MOTORS.{' '}
                            <HintTooltip
                                content={
                                    <>
                                        Gumtree reserves the right to monitor conversations sent
                                        through our servers to protect you from fraud, spam or
                                        suspicious behaviour. For more information about what we do
                                        with your personal data, see our{' '}
                                        <Link
                                            href="https://www.gumtree.com/privacy_policy"
                                            noFollow
                                        >
                                            Privacy Notice
                                        </Link>
                                        .
                                    </>
                                }
                                triggerColor="grey"
                                closeBtnColor="lightGrey"
                                theme="white"
                                elName="motors-co-uk"
                                hasCloseBtn
                                align="left"
                            />
                        </MotorsFeedSubtitle>
                    ) : (
                        <ProSellerSubtitle>
                            Your contact details will be included in your reply. Gumtree reserves
                            the right to monitor conversations sent through our servers to protect
                            you from fraud, spam or suspicious behaviour.
                        </ProSellerSubtitle>
                    )}
                </fieldset>
            )}
            <Spinner
                spinning={sendState === 'sending'}
                style={{ marginTop: 0 }}
            >
                <Button
                    // Fixes initially disabled button when savedMessage truthy
                    key={mounted ? 'forceRemount' : undefined}
                    type="button"
                    display={buttonDisplayExp}
                    fullWidth
                    disabled={!mounted || !isPhoneValid || Boolean(!savedMessage || isCurrentUserSeller)}
                    data-q="send-message-button"
                    label={
                        <>
                            <div className="loader-spinner" />
                            <span>{formType === 'mobile' ? 'Send' : 'Send Message'}</span>
                        </>
                    }
                    onClick={sendMessage}
                />
            </Spinner>
        </MessageForm>
    );
};

const Message: React.FC = () => {
    const { phoneNumber, userLoggedIn } = useSelector(({
        userData,
        sellerContactDetails,
    }: VipState) => ({
        phoneNumber: sellerContactDetails.phoneNumber,
        userLoggedIn: !!userData && userData.userLoggedIn,
    }));

    const formEl = useRef<HTMLFormElement>(null);
    const { isMotorsCTATask2 } = useContext(MotorsCTAContext);
    const [sendState, setSendState] = useState('unsent' as 'unsent' | 'sending' | 'sent');
    const [errors, setErrors] = useState([] as NameValuePair[]);
    const [savedAdvertId, storeAdvertId] = useSessionStorage(util.advertIdStorageKey);

    const [isInitialized, setIsInitialized] = useState(false);
    const [savedMessage, storeMessage] = useSessionStorage(util.messageStorageKey);

    const phoneRegx = sendMessageUtil.phoneRegexComplex;
    const defaultPhoneNumber = isMotorsCTATask2 ? phoneNumber || '' : '';

    // GrowthBook experiment: VIP phone number required A/B
    const vipPhoneRequiredVariant = useFeatureValue(GrowthBookFeature.VIP_CLICK_PHONE_NUMBER_REQUIRED, 'A') as string;
    const isVipPhoneRequired = vipPhoneRequiredVariant === 'B';

    const {
        savedPhone,
        // storePhone,
        updatePhone,
        onPhoneInputBlur,
        isPhoneValid,
        validatePhone,
    } = sendMessageUtil.useStorePhoneNumber(phoneRegx, defaultPhoneNumber, isVipPhoneRequired);

    const {
        advertId,
        proAccount,
        sellerName,
        sendReplyUrl,
        seoName,
        token,
        isMobile,
        makeAnOfferEnabled,
        hasPhone,
        isCurrentUserSeller,
        isFromMotorsFeed,
        isMotors,
        replyConfirmationTracking,
    } = useSelector(util.selectStaticData, shallowEqual);
    const { openDialog } = useModal();

    const formType: FormType = isMobile ? (proAccount ? 'mobile-pro' : 'mobile') : 'desktop';
    const hasError = errors.some(({ value }) => value);

    const placeholder = useMemo(
        () => util.getPlaceholderText(isMobile, seoName, sellerName),
        [isMobile]
    );

    useEffect(() => {
        if (!savedAdvertId || savedAdvertId !== advertId) {
            storeMessage(placeholder || null);
        }
        storeAdvertId(advertId);
        setIsInitialized(true);
    }, [advertId, savedAdvertId, placeholder]);

    useEffect(() => {
        return util.smoothScrollFromSticky(formEl.current!);
    }, []);

    const dispatch = useDispatch() as VipUseDispatch;
    const sendMessage = 
        async (e: MouseEvent<HTMLButtonElement>) => {
            if (isFromMotorsFeed && hasPhone && !validatePhone()) return;

            if (!userLoggedIn) {
                const redirectUrl = isFromMotorsFeed ? `/reply-message/${advertId}` : undefined;
                openDialog(e, LoginModalType.EMAIL, true, redirectUrl);
                return;
            }

            // task1 temporary solution
            if (isFromMotorsFeed && isMobile) {
                trackGA4Event<GA4.CtaPostCallReminderEvent>({
                    event: 'cta_post_call_reminder_click',
                    clickElement: 'send message button',
                });
            }

            const send = async () => {
                setSendState('sending');
                const phoneNumber =
                    (proAccount || isFromMotorsFeed) && hasPhone && savedPhone ? savedPhone : undefined;

                try {
                    const { replyConfirmationUrl } = await util.sendMessage({
                        advertId,
                        message: savedMessage || '',
                        sendReplyUrl,
                        phoneNumber,
                        token,
                    });
                    storeMessage(null);
                    storeAdvertId(null);
                    trackPermutiveCustomEvent(
                        window,
                        PermutiveEventNames.R2S_EMAIL_BEGIN,
                        {},
                        true
                    );

                    trackGA4PreNav<GA4.ContactSellerMessageEvent>({
                        event: 'contact_a_seller',
                        contactASellerMethod: 'send a message',
                        formName: 'contact a seller',
                        formStep: undefined,
                        formValidation: 'success',
                        formError: undefined,
                        listingDetails: replyConfirmationTracking,
                        listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                        requestId: getRequestId(),
                        isPhoneNumberFilled: phoneNumber ? 'yes' : 'no',
                        isMessageTextFilledValid: (savedMessage && placeholder !== savedMessage) ? 'yes' : 'no',
                    });

                    dispatch(navigate(replyConfirmationUrl));
                } catch (e) {
                    const errorMessage = `${
                        typeof e === 'string' ? e : 'Unexpected error'
                    }, please try again`;

                    setErrors(
                        e.errors || [
                            {
                                name: 'sendError',
                                value: errorMessage,
                            },
                        ]
                    );

                    trackGA4Event<GA4.ContactSellerMessageEvent>({
                        event: 'contact_a_seller',
                        contactASellerMethod: 'send a message',
                        formName: 'contact a seller',
                        formStep: undefined,
                        formValidation: 'failure',
                        formError: errorMessage,
                        listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                        listingDetails: {},
                    });

                    console.error(e);
                } finally {
                    setSendState('sent');
                }
            };
            await send();
    };

    const updateMessage = useCallback(
        ({ currentTarget: { value } }: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            storeMessage(value);
        },
        []
    );

    const handleUpdatePhone = (e: FormEvent<HTMLInputElement>) => {
        updatePhone(e);
    };

    const handlePhoneInputBlur = () => {
        onPhoneInputBlur();
    };

    return (
        <MessageFormComponent
            formRef={formEl}
            formType={formType}
            canOffer={makeAnOfferEnabled}
            hasError={hasError}
            isMobile={isMobile}
            savedMessage={savedMessage}
            isCurrentUserSeller={isCurrentUserSeller}
            updateMessage={updateMessage}
            errors={errors}
            proAccount={proAccount}
            hasPhone={hasPhone}
            isPhoneValid={isPhoneValid}
            savedPhone={savedPhone}
            updatePhone={handleUpdatePhone}
            onPhoneInputBlur={handlePhoneInputBlur}
            sendState={sendState}
            mounted={isInitialized}
            sendMessage={sendMessage}
            isFromMotorsFeed={isFromMotorsFeed}
            isMotors={isMotors}
            isPhoneRequired={isVipPhoneRequired}
        />
    );
};

export default Message;
